import
{
  Component,
  QueryList,
  ContentChildren,
  AfterContentInit,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChildren,
  ElementRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';

import { Subject } from 'rxjs';
import { filter, tap, takeUntil } from 'rxjs/operators';

import { TabComponent } from './tab/tab.component';
import { PopupMenuConfig } from '../popup-menu/cog/popup-menu.config';
import { PopupMenuService } from '../popup-menu/cog/popup-menu.service';
import { AppConstants } from 'src/app/_apps/app.constants';
import { CloseEventType, PopupMenuRef } from '@ui/misc/popup-menu';

@Component({
    selector: 'aa-tab-group',
    templateUrl: './tab-group.component.html',
    styleUrls: [
        './tab-group.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabGroupComponent implements OnInit, AfterContentInit, OnDestroy
{
    @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;
    @ViewChildren('tabRefs') tabRefs: QueryList<ElementRef>;
    @Input() justified: boolean = false;
    @Input() customCls: string = '';
    @Output() selectedTab: EventEmitter<any> = new EventEmitter();
    private destroyator = new Subject();
    private oldTab: TabComponent = undefined;
    private newTab: TabComponent = undefined;
    private configTapPopu: PopupMenuConfig = {
        class: 'sub-menu',
        positions: [
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 2.5, // Épaisseur de la bordure du bas
                offsetX: 0
            }
        ],
        typePopup: 'poptab'
    } as PopupMenuConfig;
    private popup: PopupMenuRef;

    constructor (
        private router: Router,
        private location: Location,
        private popupMenuService: PopupMenuService
    )
    { }

    ngOnInit ()
    {
        this.router.events
            .pipe(
                filter((evt) => evt instanceof NavigationEnd),
                tap(() =>
                {
                    const routedTab: TabComponent = this.findRoutedTab(this.router.url);

                    if (routedTab !== undefined)
                    {
                        this.activateTab(routedTab);
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngAfterContentInit ()
    {
        const routedTab: TabComponent = this.findRoutedTab(this.router.url);

        this.tabs
            .forEach((tab, idx) =>
                tab.id = idx
            );
        if (routedTab !== undefined)
        {
            this.activateTab(routedTab);
        }
        else
        {
            const activeTab: TabComponent = this.tabs
                .find((tab) =>
                    tab.active
                );

            if (activeTab !== undefined)
            {
                this.selectTab(this.tabs.first);
            }
        }
    }
    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    onClickTab (tab: TabComponent): void
    {
        if (tab.tabRoute !== undefined)
        {
            if (!tab.tabRoute.includes(AppConstants.FullParamPlaceholderUrl))
            {
                this.router.navigateByUrl(tab.tabRoute);
                this.selectTab(tab);
            }
            else
            {
                this.activateTab(tab);
            }
        }
        else
        {
            const eltRef = this.tabRefs.toArray()[tab.id].nativeElement.querySelector('.aa-tab-title');

            this.activateTab(tab);
            if (this.popup)
            {
                this.popup.close(CloseEventType.Clicked);
            }

            this.popup = this.popupMenuService.create(eltRef, this.configTapPopu, tab.subTabItems);
            this.popup.closeEvent
                .pipe(
                    filter((evt: CloseEventType) =>
                        evt === CloseEventType.TimeOut || evt === CloseEventType.Outside
                    ),
                    tap(() =>
                        this.selectTab(this.oldTab)
                    ),
                    takeUntil(this.destroyator)
                )
                .subscribe();
        }
    }

    selectTab (tab: TabComponent): void
    {
        if (tab.tabRoute)
        {
            this.location.replaceState(tab.tabRoute);
        }

        this.activateTab(tab);
    }

    private findRoutedTab (url: string): TabComponent | undefined
    {
        return this.tabs
            .find((tab: TabComponent) =>
                this.matchRoute(url, tab)
            );
    }

    private matchRoute (url: string, tab: TabComponent): boolean
    {
        if (tab.activeWhen)
        {
            const regexp = new RegExp(tab.activeWhen);

            return regexp.test(url);
        }

        return url.startsWith(tab.tabRoute);
    }

    private activateTab (tab: TabComponent): void
    {
        this.oldTab = this.newTab;
        this.newTab = tab;
        const oldId: number = this.oldTab ? this.oldTab.id : -1;
        const newId: number = this.newTab.id;

        if (newId !== oldId)
        {
            this.selectedTab.emit({ oldTab: this.oldTab, newTab: this.newTab });
            this.tabs.toArray()
                .forEach((t: TabComponent) =>
                    t.active = false
                );
            tab.active = true;
        }
    }
}
